import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { IThunkAPI } from '@/store/types';
import type {
  IEducation,
  IEducationOptions,
  ILanguageAndProficiency,
  ILanguageOptions,
  IProficiencyAreaOptions,
  IProficiencyOptions,
  ISetProficiency,
  ISkillsInitialState,
  IUserProficiencies,
} from '@/types/resume-builder.model';

import { resumeBuilderApis } from '@/api/resume-builder';

export const initialState: ISkillsInitialState = {
  educationOptions: [],
  education: undefined,
  educationId: 0,
  languageAndProficiency: {
    languageOptions: [],
    proficiencyAreasOptions: [],
    proficiencyOptions: [],
    userProficiencies: [],
    userProficienciesCopy: [],
  },
};

export const getEducationOptions = createAsyncThunk<IEducationOptions[], undefined, IThunkAPI>(
  'utils/educationLevel/list',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getEducationOptions();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getLanguageOptions = createAsyncThunk<ILanguageOptions[], undefined, IThunkAPI>(
  '/utils/language/list',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getLanguageOptions();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getProficiencyAreaOptions = createAsyncThunk<IProficiencyAreaOptions[], undefined, IThunkAPI>(
  '/utils/proficiencyArea/list',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getProficiencyAreaOptions();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getProficiencyOptions = createAsyncThunk<IProficiencyOptions[], undefined, IThunkAPI>(
  '/utils/languageproficiency/list',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getProficiencyOptions();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setLanguageProficiencies = createAsyncThunk<unknown, ISetProficiency[], IThunkAPI>(
  '/member/set/languageProficiencies',
  async (payload, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setLanguageProficiencies(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getMemberSkills = createAsyncThunk<IUserProficiencies, undefined, IThunkAPI>(
  '/member/get/skills',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getMemberSkills();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setMemberSkills = createAsyncThunk<unknown, IEducation, IThunkAPI>(
  '/member/set/skills',
  async (payload, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setMemberSkills(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const slice = createSlice({
  name: 'skills',
  initialState,
  reducers: {
    setUserProficiencies(state: ISkillsInitialState, action: PayloadAction<ILanguageAndProficiency[]>) {
      state.languageAndProficiency.userProficiencies = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getEducationOptions.fulfilled, (state, action) => {
        state.educationOptions = action.payload;
      })
      .addCase(getLanguageOptions.fulfilled, (state, action) => {
        state.languageAndProficiency.languageOptions = action.payload;
      })
      .addCase(getProficiencyAreaOptions.fulfilled, (state, action) => {
        state.languageAndProficiency.proficiencyAreasOptions = action.payload;
      })
      .addCase(getProficiencyOptions.fulfilled, (state, action) => {
        state.languageAndProficiency.proficiencyOptions = action.payload;
      })
      .addCase(getMemberSkills.fulfilled, (state, action) => {
        state.languageAndProficiency.userProficiencies = action.payload.languageProficiencies;
        state.languageAndProficiency.userProficienciesCopy = action.payload.languageProficiencies;
        state.education = action.payload.education;
        state.educationId = action.payload.education?.educationId || 0;
      });
  },
});
export const { setUserProficiencies } = slice.actions;
export default slice.reducer;
